import { Box, Tooltip } from "@mui/material";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericTitleHeader } from "../common/GenericTitleHeader";
import CarrierAction from "../../service/carrier.action";
import { ValueFormatterParams } from "ag-grid-community";
import SearchCarrierModal from "./searchCarrierModal";
import { ContextOperation } from "../../context/OperationContext";
import { saveFileFromResponse } from "../../utils";
import { CarrierList } from "./Type";
import { nameof } from "../common/ObjectUtils";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import AgGridOperationTable from "../common/AgGridOperationTable";
import EditIcon from "@mui/icons-material/Edit";
import useSnackbar from "../../hooks/useSnackbar";
import EditCarrierModal from "./editCarrierModal";
import * as Mui from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EnumChangeName from "../common/EnumChangeName";
import SectionHeader from "../../../../util/component/SectionHeader";

const CarrierTable = () => {
  const { setLoading } = useContext(ContextOperation);
  const snackbar = useSnackbar();
  const [searchData, setSearchData] =
    useState<CarrierList.SearchFunctionDataType>({});
  const [formData, setFormData] = useState<CarrierList.SearchFunctionDataType>(
    {}
  );
  const { tenant } = useContext(ContextOperation);
  const [carrierEditData, setCarrierEditData] =
    useState<CarrierList.EditCarrierType>();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const carrierAction = CarrierAction();

  const { t } = useTranslation(["operation"]);

  const columns: any = [
    {
      field: nameof<CarrierList.CarrierTypes>("name"),
      headerName: t("carrier.table.name"),
      minWidth: 150,
      sortable: true,
      cellRenderer: (params: any) => (
        <Tooltip title={!!params.data.name ? params.data.name : ""}>
          <span>{!!params.data.name ? params.data.name : ""}</span>
        </Tooltip>
      ),
    },
    {
      field: nameof<CarrierList.CarrierTypes>("supplierAddress"),
      headerName: t("carrier.table.supplierAddress"),
      minWidth: 230,
      cellRenderer: (params: any) => (
        <>
          <Tooltip
            title={
              !!params.data.supplier.address ? params.data.supplier.address : ""
            }
          >
            <span>
              {!!params.data.supplier.address
                ? params.data.supplier.address
                : ""}
            </span>
          </Tooltip>
        </>
      ),
    },
    {
      field: nameof<CarrierList.CarrierTypes>("vatNumber"),
      headerName: t("carrier.table.vatNumber"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("bpCode"),
      headerName: t("carrier.table.bpCode"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("identificationCode"),
      headerName: t("carrier.table.identificationCode"),
      minWidth: 230,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("oldIdentificationCode"),
      headerName: t("carrier.table.oldIdentificationCode"),
      minWidth: 270,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("bpKind"),
      headerName: t("carrier.table.bpKindCode"),
      cellRenderer: (params: any) => (
        <span>{!!params.data.bpKind.code ? params.data.bpKind.code : ""}</span>
      ),
      minWidth: 170,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("bpKind"),
      headerName: t("carrier.table.bpKindDescription"),
      cellRenderer: (params: any) => (
        <span>
          {!!params.data.bpKind.description
            ? params.data.bpKind.description
            : ""}
        </span>
      ),
      minWidth: 220,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("serviceType"),
      headerName: t("carrier.table.serviceTypeCode"),
      valueFormatter: (
        params: ValueFormatterParams<CarrierList.CarrierTypes>
      ) =>
        params.data?.serviceType
          ?.map((val: any) => `${val.code}`)
          .join(" , ") || "",
      minWidth: 250,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("serviceType"),
      headerName: t("carrier.table.serviceTypeDescription"),
      valueFormatter: (
        params: ValueFormatterParams<CarrierList.CarrierTypes>
      ) =>
        params.data?.serviceType
          ?.map((val: any) => `${val.description}`)
          .join(" , ") || "",
      minWidth: 300,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("category"),
      headerName: t("carrier.table.categoryCode"),
      valueFormatter: (
        params: ValueFormatterParams<CarrierList.CarrierTypes>
      ) =>
        params.data?.category?.map((val: any) => `${val.code}`).join(" , ") ||
        "",
      minWidth: 170,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("category"),
      headerName: t("carrier.table.categoryDescription"),
      valueFormatter: (
        params: ValueFormatterParams<CarrierList.CarrierTypes>
      ) =>
        params.data?.category
          ?.map((val: any) => `${val.description}`)
          .join(" , ") || "",
      minWidth: 220,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("fax"),
      headerName: t("carrier.table.fax"),
      minWidth: 100,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("phoneNumber"),
      headerName: t("carrier.table.phoneNumber"),
      minWidth: 200,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("cellPhoneNumber"),
      headerName: t("carrier.table.cellPhoneNumber"),
      minWidth: 250,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("email"),
      headerName: t("carrier.table.email"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data.email}>
          <span>{params.data.email}</span>
        </Tooltip>
      ),
    },
    {
      field: nameof<CarrierList.CarrierTypes>("iban"),
      headerName: t("carrier.table.iban"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data.iban}>
          <span>{params.data.iban}</span>
        </Tooltip>
      ),
    },
    {
      field: nameof<CarrierList.CarrierTypes>("active"),
      headerName: t("carrier.table.active"),
      cellRenderer: (params: any) => (
        <span>
          {params.data.active !== undefined
            ? EnumChangeName().booleanType(params.data.active.toString())
            : ""}
        </span>
      ),
      minWidth: 120,
    },
    {
      field: nameof<CarrierList.CarrierTypes>("supplierTaxCode"),
      headerName: t("carrier.table.supplierTaxCode"),
      cellRenderer: (params: any) => (
        <span>
          {!!params.data.supplier.taxCode ? params.data.supplier.taxCode : ""}
        </span>
      ),
      minWidth: 250,
    },

    // {
    //   field: "_actions",
    //   headerName: "",
    //   pinned: "right",
    //   minWidth: 80,
    //   cellRenderer: (params: any) => {
    //     return (
    //       <Tooltip title={t("tooltipTitle.editShowAction")}>
    //         <EditIcon
    //           style={{
    //             color: "#C8017D",
    //           }}
    //           onClick={() => editCarrierData(params?.data)}
    //         />
    //       </Tooltip>
    //     );
    //   },
    // },
  ];

  const editCarrierData = (params: any) => {
    setLoading(true);
    carrierAction
      .getCarrierById(params?.id, tenant)
      .then((response: any) => {
        setLoading(false);
        setCarrierEditData({
          id: response?.id,
          currentStatus: response?.currentStatus,
          active: response?.active,
        });
        setOpenEditModal(true);
      })
      .catch((response: any) => {
        setLoading(false);
      });
  };

  const updateCarrierData = (editData: CarrierList.EditCarrierType) => {
    setLoading(true);
    carrierAction
      .updateCarrier(editData.id, editData, tenant)
      .then((response: any) => {
        setLoading(false);
        refreshDatasource();
        snackbar.show("success", t("carrier.table.editActionSuccessMessage"));
      })
      .catch((error: any) => {
        if (error.response) {
          setLoading(false);
          snackbar.show("error", error.response.data.error);
        } else if (error.request) {
          setLoading(false);
          snackbar.show("error", t("networkErrorMessage"));
        } else setLoading(false);
      });
  };

  const exportCarrier = async () => {
    setLoading(true);
    carrierAction
      .exportCarrier(tenant, formData)
      .then((response: any) => {
        saveFileFromResponse(response);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        snackbar.show("error", t("missingEquipment.errors.actionError"));
      });
  };

  const [datasource, _totalElements, refreshDatasource] =
    useAgGridCustomDatasource(
      carrierAction.getCarrier,
      [tenant, formData],
      [tenant, formData]
    );

  const changeActiveDisplay = (data: boolean) => {
    switch (data) {
      case true:
        return t("internOptionSelectName.true");
      case false:
        return t("internOptionSelectName.false");
      default:
        return "";
    }
  };

  const resetData = () => {
    setSearchData({});
    setFormData({});
  };

  return (
    <Box>
      <SectionHeader
          title={t("carrier.titleFirst")}
      />
      <SearchCarrierModal
        searchData={searchData}
        setSearchData={setSearchData}
        setFormData={setFormData}
        resetData={resetData}
        refreshTable={refreshDatasource}
      />
      <Mui.Card>
        <Mui.CardContent>
          <Mui.Box
            sx={{
              backgroundColor: "white",
              border: " 1px solid #dbe4e9",
              borderRadius: "2px",
              padding: "9px 1px 3px 2px",
            }}
          >
            <Mui.Grid
              container
              spacing={2}
              alignItems="center"
              alignContent="center"
              direction="row"
            >
              <Mui.Grid item xs={6}></Mui.Grid>
              <Mui.Grid item xs={6}>
                <Mui.Box
                  sx={{
                    justifyContent: "flex-end",
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <Mui.Stack direction="row" alignItems="center">
                    <Mui.Button
                      size="icon"
                      variant="text"
                      disabled={_totalElements ? false : true}
                      onClick={exportCarrier}
                      color="accent"
                    >
                      <Mui.Tooltip title={t("tooltipTitle.download")}>
                        <FileDownloadIcon />
                      </Mui.Tooltip>
                    </Mui.Button>
                  </Mui.Stack>
                </Mui.Box>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Box>
          <Mui.Grid>
		  

            <AgGridOperationTable
              columnDefs={columns}
              serverSideDatasource={datasource}
              rowStyle={{ width: "300%" }}
              totalElements={_totalElements}
            />

          </Mui.Grid>
        </Mui.CardContent>
      </Mui.Card>
      {openEditModal && (
        <EditCarrierModal
          carrierEditData={carrierEditData}
          setCarrierEditData={setCarrierEditData}
          setOpenEditModal={setOpenEditModal}
          openEditModal={openEditModal}
          updateCarrierData={updateCarrierData}
        />
      )}
    </Box>
  );
};

export default CarrierTable;
